import MuxPlayer from '@mux/mux-player-react';
import { useVideoCreatorStore } from '@src/stores-v2';
import { useEffect, useRef, useState } from 'react';
import { VideoSeekSlider } from 'react-video-seek-slider';
import styled from 'styled-components';

import {
  getPlayerStyle,
  PlayerContainer as PlayerContainerBase,
  ProgressBarContainer as ProgressBarContainerBase,
} from './MuxPlayerClip';

import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { AspectRatio } from '@src/types.ts/video';

export type Cut = {
  source: string;
  trim_start: number;
  duration: number;
  id: string | undefined;
};

type Props = {
  cuts: Cut[];
  poster: string | undefined;
  autoPlay: boolean;
  onPlay?: () => void;
  /** Fires when the active cut's video metadata is loaded (player usable). */
  onLoadedMetadata?: () => void;
  isPlaying?: boolean;
  muxPlaybackId?: string;
  aspectRatio?: AspectRatio;
  paused?: boolean;
  /** Master playback volume 0..1 for the active cut. Defaults to full. */
  volume?: number;
  /** Master mute for the active cut (inactive cuts are always muted). */
  muted?: boolean;
};

const AVERAGE_TIME_UPDATE_DELTA = 0.265; // padding to move to the next cut more smoothly

/**
 * The idea is to render multiple MuxPlayer elements, each representing a cut
 * of the video.
 * The player will switch between cuts based on the current time of the video.
 * The reason for using multiple MuxPlayer elements is to load all of the cuts
 * up-front for a smoother playback.
 */
const MuxPlayerMultiClip = ({
  cuts,
  poster,
  autoPlay,
  onPlay,
  onLoadedMetadata,
  isPlaying,
  muxPlaybackId,
  aspectRatio,
  paused,
  volume,
  muted,
}: Props) => {
  const videoCreator = useVideoCreatorStore();
  const [currentCutIndex, setCurrentCutIndex] = useState(0);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [isShowingPoster, setIsShowingPoster] = useState(true);
  const playerListRef = useRef<(MuxPlayerElement | null)[]>([]);

  const totalDuration = cuts.reduce((acc, cut) => acc + cut.duration, 0);

  useEffect(() => {
    const playerRef = playerListRef.current[currentCutIndex];
    if (!isPlaying && playerRef && typeof playerRef.pause === 'function') {
      playerRef.pause();
    }
  }, [isPlaying]);

  const onMuxTimeUpdate = (event: MuxPlayerElementEventMap['timeupdate']) => {
    const videoElement = event.target as HTMLVideoElement;
    const seconds = videoElement.currentTime;
    const playerRef = playerListRef.current[currentCutIndex];
    const currentCut = cuts[currentCutIndex];

    if (
      seconds > currentCut.trim_start &&
      !playerRef?.paused &&
      isShowingPoster
    ) {
      setIsShowingPoster(false);
    }

    const endTime = currentCut.trim_start + currentCut.duration;
    // we are at the end of the current cut
    if (seconds >= endTime - AVERAGE_TIME_UPDATE_DELTA) {
      // go to the next cut if it exists
      if (currentCutIndex < cuts.length - 1) {
        playerListRef.current[currentCutIndex]?.pause();
        playerListRef.current[currentCutIndex + 1]?.play();
        setCurrentCutIndex(currentCutIndex + 1);
      } else {
        // otherwise stop playback if we are at the end of the last cut
        playerRef!.pause();
        setCurrentCutIndex(0);
        // reset each cut back to the begging
        for (let i = 0; i < cuts.length; i++) {
          const cutPlayerRef = playerListRef.current[i];
          if (cutPlayerRef) {
            cutPlayerRef.currentTime = cuts[i].trim_start || 0;
          }
        }
      }
    }

    const timeInClip =
      cuts
        .slice(0, currentCutIndex)
        .reduce((acc, cut) => acc + cut.duration, 0) +
      (seconds - currentCut.trim_start);
    setCurrentTimeMs(timeInClip * 1000);
  };

  const onSeekSliderChange = (newTimeMs: number) => {
    const newTimeSec = newTimeMs / 1000;
    const paused = playerListRef.current[currentCutIndex]?.paused;
    let accumulatedDuration = 0;
    let cutFound = false;
    for (let i = 0; i < cuts.length; i++) {
      const cut = cuts[i];
      const withinCut = newTimeSec < accumulatedDuration + cut.duration;
      if (!cutFound && withinCut) {
        if (i !== currentCutIndex) {
          playerListRef.current[currentCutIndex]?.pause();
        }

        const timeInCut = newTimeSec - accumulatedDuration;
        const nextPlayerRef = playerListRef.current[i];
        if (nextPlayerRef) {
          nextPlayerRef.currentTime = cut.trim_start + timeInCut;
          if (nextPlayerRef.paused && !paused) {
            nextPlayerRef.play();
          }
        }

        setCurrentCutIndex(i);

        cutFound = true;
      } else {
        // reset rest of cuts to their start time
        const cutPlayerRef = playerListRef.current[i];
        if (cutPlayerRef) {
          cutPlayerRef.currentTime = cut.trim_start;
        }
      }
      accumulatedDuration += cut.duration;
    }
  };

  return (
    <PlayerContainer onClick={e => e.stopPropagation()}>
      {cuts.map((cut, idx) => (
        <MuxPlayer
          key={cut.id || idx}
          style={getPlayerStyle(
            isShowingPoster,
            aspectRatio || videoCreator.getDefaultAspectRatio(),
          )}
          streamType="on-demand"
          playbackId={muxPlaybackId}
          src={!muxPlaybackId ? cut.source : undefined}
          startTime={cut.trim_start}
          poster={poster}
          onPlay={onPlay}
          volume={volume ?? 1}
          ref={ref => (playerListRef.current[idx] = ref)}
          className={`mux-player${
            idx === currentCutIndex ? '' : ' mux-player--inactive'
          }`}
          onTimeUpdate={idx === currentCutIndex ? onMuxTimeUpdate : () => {}}
          onLoadedMetadata={
            idx === currentCutIndex ? onLoadedMetadata : undefined
          }
          autoPlay={idx === currentCutIndex ? autoPlay : false}
          muted={idx !== currentCutIndex || (muted ?? false)}
          preload="auto"
          paused={paused}
        />
      ))}
      <ProgressBarContainer>
        <VideoSeekSlider
          max={totalDuration * 1000}
          currentTime={currentTimeMs}
          onChange={onSeekSliderChange}
          secondsPrefix="00:"
          key={currentCutIndex} // need key because this component memoizes stale onSeekSliderChange
        />
      </ProgressBarContainer>
    </PlayerContainer>
  );
};

const PlayerContainer = styled(PlayerContainerBase)`
  .mux-player {
    position: absolute;
    z-index: 1;
  }
  .mux-player--inactive {
    visibility: hidden;
  }
`;

const ProgressBarContainer = styled(ProgressBarContainerBase)`
  z-index: 2;
`;

export default MuxPlayerMultiClip;
